import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, CalendarDays, TrendingUp, Megaphone } from 'lucide-react';
import AppLogo from '@/components/AppLogo';
import SiteFooter from '@/components/SiteFooter';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const LEVEL_STYLES = {
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  extreme: 'bg-rose-50 text-rose-700 border-rose-200',
};

function LevelChip({ level }) {
  return (
    <span className={`inline-block text-[11px] font-semibold uppercase px-2 py-0.5 rounded border ${LEVEL_STYLES[level] || LEVEL_STYLES.medium}`}>
      {level}
    </span>
  );
}

export default function ThanksgivingForecastPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API}/airport/thanksgiving-forecast`);
        if (r.ok && !cancel) setData(await r.json());
      } catch { /* static content still renders */ }
      if (!cancel) setLoading(false);
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="thanksgiving-forecast-page">
      <header className="container mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
            <AppLogo size={36} className="flex-shrink-0" />
            <span className="heading text-[13px] sm:text-base font-semibold tracking-tight text-slate-900">
              How Busy Is My Airport
            </span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Predictor
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600 mb-2 flex items-center gap-2">
              <CalendarDays className="w-4 h-4" /> Seasonal Forecast · Updated for 2026
            </p>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Thanksgiving 2026 Airport Crowd Forecast
            </h1>
            <p className="text-lg text-slate-600">
              Hour-by-hour crowd projections for the 16 busiest US airports across the three
              heaviest travel days of Thanksgiving week — modeled from scheduled flight density,
              aircraft capacity, and historical holiday patterns.
            </p>
          </div>

          {/* Static editorial — prerendered for crawlers */}
          <section className="prose-slate mb-10 space-y-4 text-slate-700 leading-relaxed">
            <h2 className="text-2xl font-semibold text-slate-900">The national picture</h2>
            <p>
              Thanksgiving 2026 falls on <strong>Thursday, November 26</strong>, and the travel
              pattern around it is the most predictable crowd event in US aviation. The Wednesday
              before (Nov 25) compresses what is normally a full week of leisure departures into a
              single day, and the Sunday after (Nov 29) is historically the heaviest single air-travel
              day of the entire year — because nearly everyone must be home for work on Monday.
              Thanksgiving Day itself is the sleeper: it is reliably one of the calmest,
              cheapest flying days of November.
            </p>
            <p>
              What makes holiday week different from a normal busy week is the <em>shape</em> of the
              day, not just its size. On ordinary days our model shows a mid-morning trough between
              roughly 9:30 and 11 AM. On Thanksgiving Eve and Return Sunday that trough largely
              disappears at connection-heavy hubs — security demand runs near peak from the first
              5 AM bank until early evening. The practical consequences: arrive earlier than you
              think you need to, avoid checked bags where possible, and treat any layover under an
              hour as a genuine risk (our <Link to="/blog/layover-connection-time-guide" className="text-blue-600 underline">layover guide</Link> explains the math).
            </p>
            <p>
              The table below is generated by the same engine that powers our{' '}
              <Link to="/" className="text-blue-600 underline">live predictor</Link>: scheduled
              departure density weighted by aircraft size, adjusted for time of day and day of week.
              For background on how the model works — and its limitations — see our{' '}
              <Link to="/methodology" className="text-blue-600 underline">methodology page</Link>.
              For the full holiday strategy, read{' '}
              <Link to="/blog/thanksgiving-christmas-airport-crowds" className="text-blue-600 underline">
                Thanksgiving vs. Christmas: Which Holiday Actually Wrecks Airports?
              </Link>
            </p>
          </section>

          {/* National summary chips */}
          {data?.national && (
            <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="tg-national-summary">
              <Card className="border-rose-200 bg-rose-50/50">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">Busiest day nationally</p>
                  <p className="text-lg font-bold text-slate-900">{data.national.busiest_day}</p>
                  <p className="text-xs text-slate-500">{data.national.busiest_day_tag}</p>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-1">Calmest day to fly</p>
                  <p className="text-lg font-bold text-slate-900">{data.national.calmest_day}</p>
                  <p className="text-xs text-slate-500">{data.national.calmest_day_tag}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Per-hub forecast */}
          <Card className="border-slate-200 bg-white mb-10">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                <h2 className="heading text-lg font-semibold text-slate-900">Hub-by-hub forecast</h2>
              </div>
              {loading && <p className="text-sm text-slate-500 py-6 text-center">Computing forecast…</p>}
              {!loading && !data && (
                <p className="text-sm text-slate-500 py-6 text-center">
                  Forecast temporarily unavailable — try refreshing.
                </p>
              )}
              {data?.hubs && (
                <div className="overflow-x-auto" data-testid="tg-forecast-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-3">Airport</th>
                        <th className="py-2 pr-3">Wed Nov 25 peak</th>
                        <th className="py-2 pr-3">Sun Nov 29 peak</th>
                        <th className="py-2 pr-3">Calmest window (Wed)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.hubs.map((h) => {
                        const wed = h.days.find((d) => d.tag === 'Thanksgiving Eve');
                        const sun = h.days.find((d) => d.tag === 'Return Sunday');
                        return (
                          <tr key={h.code} data-testid={`tg-row-${h.code}`}>
                            <td className="py-2.5 pr-3">
                              <Link to={`/airports/${h.code.toLowerCase()}`} className="font-mono font-semibold text-blue-600 hover:underline">
                                {h.code}
                              </Link>
                              <span className="hidden sm:inline text-slate-500"> {h.city}</span>
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                              {wed && <>{wed.peak_window} <LevelChip level={wed.peak_level} /></>}
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                              {sun && <>{sun.peak_window} <LevelChip level={sun.peak_level} /></>}
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap text-emerald-700">
                              {wed?.calm_window}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* More static editorial */}
          <section className="mb-10 space-y-4 text-slate-700 leading-relaxed">
            <h2 className="text-2xl font-semibold text-slate-900">How to use this forecast</h2>
            <p>
              <strong>If you're flying Wednesday:</strong> book the first bank of the morning or fly
              during your airport's calm window above. Arrive 30–45 minutes earlier than your normal
              cushion — the peak windows in the table are when checkpoint demand crests, and TSA
              staffing rarely scales fully with holiday volume.
            </p>
            <p>
              <strong>If you're flying Return Sunday:</strong> this is the one day of the year the
              classic "get there very early" advice is fully justified. Morning departures clear the
              airport before the afternoon crush; if you're on a 3–6 PM departure at a megahub like{' '}
              <Link to="/airports/atl" className="text-blue-600 underline">ATL</Link> or{' '}
              <Link to="/airports/dfw" className="text-blue-600 underline">DFW</Link>, treat the
              airport itself as part of the journey and plan accordingly. Consider sliding your
              return to Monday or Tuesday — fares and crowds both drop sharply.
            </p>
            <p>
              <strong>If you can fly Thanksgiving Day:</strong> do it. Terminals are calm, fares are
              typically the lowest of the week, and you'll land in time for dinner.
            </p>
            <p className="text-sm text-slate-500">
              Forecast is deterministic and refreshed weekly as airline schedules firm up. Check your
              specific flight with the <Link to="/" className="text-blue-600 underline">live predictor</Link>{' '}
              in the days before travel, when real-time FlightAware data takes over.
            </p>
          </section>

          {/* Press box */}
          <Card className="border-blue-200 bg-blue-50/50" data-testid="tg-press-box">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <Megaphone className="w-4 h-4 text-blue-600" />
                <h2 className="heading text-base font-semibold text-slate-900">Journalists & media</h2>
              </div>
              <p className="text-sm text-slate-600 mb-3">
                This forecast is free to cite with attribution to <strong>HowBusyIsMyAirport.com</strong>.
                We can provide city-specific breakdowns, hour-by-hour charts for any of 331 airports,
                and interviews about holiday travel patterns.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                Contact us for data or interviews <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
